/**
 * systemFields — the fixed core fields every contract form carries.
 *
 * These live outside the editable schema: they are hardcoded in ContractForm
 * (INITIAL_CORE) and rendered before any schema section. The builder shows
 * them on the canvas as a read-only preview so the user edits the *whole*
 * form rather than only the custom half — but they are never part of
 * `schema.sections`, so they are never saved, reordered, or deleted.
 *
 * The groups mirror ContractForm's four core sections one to one — same
 * titles, same colors, same fields in the same order — so the preview always
 * reads exactly like the real form. Labels reuse the `contractForm.*` i18n
 * keys, in whichever language the user is on.
 */

export const SYSTEM_SECTION_ID = '__system__';

export const SYSTEM_SECTIONS = [
  {
    id: 'sys-identity',
    titleKey: 'contractForm.counterpartyIdentity',
    subtitleKey: 'contractForm.counterpartyIdentitySubtitle',
    color: '#6366f1',
    fields: [
      { id: 'sys-contractName', labelKey: 'contractForm.contractName', type: 'text', required: true, width: 'full' },
      { id: 'sys-contractNumber', labelKey: 'contractForm.contractNumber', type: 'text', required: false, width: 'half' },
      { id: 'sys-contractDate', labelKey: 'contractForm.contractDate', type: 'date', required: true, width: 'half' },
      { id: 'sys-contractType', labelKey: 'contractForm.contractType', type: 'select', required: true, width: 'half' },
      { id: 'sys-responsibleDepartment', labelKey: 'contractForm.responsibleDepartment', type: 'select', required: false, width: 'half' },
      { id: 'sys-templateSource', labelKey: 'contractForm.templateSource', type: 'select', required: false, width: 'half' },
      { id: 'sys-responsiblePerson', labelKey: 'contractForm.responsiblePerson', type: 'select', required: false, width: 'half' },
      { id: 'sys-priority', labelKey: 'contractForm.priority', type: 'select', required: false, width: 'half' },
      { id: 'sys-confidentiality', labelKey: 'contractForm.confidentialityLevel', type: 'select', required: false, width: 'half' },
      { id: 'sys-signedContract', labelKey: 'contractForm.signedContract', type: 'checkbox', required: false, width: 'half' },
    ],
  },
  {
    id: 'sys-counterparty',
    titleKey: 'contractForm.counterpartySection',
    subtitleKey: 'contractForm.counterpartySectionSubtitle',
    color: '#8b5cf6',
    fields: [
      { id: 'sys-counterpartyType', labelKey: 'contractForm.counterpartyType', type: 'select', required: true, width: 'half' },
      { id: 'sys-counterparty', labelKey: 'contractForm.counterparty', type: 'text', required: true, width: 'half' },
      { id: 'sys-contactPerson', labelKey: 'contractForm.contactPerson', type: 'text', required: false, width: 'half' },
      { id: 'sys-email', labelKey: 'contractForm.email', type: 'email', required: false, width: 'half' },
      { id: 'sys-phone', labelKey: 'contractForm.phone', type: 'phone', required: false, width: 'half' },
      { id: 'sys-country', labelKey: 'contractForm.country', type: 'text', required: false, width: 'half' },
      { id: 'sys-city', labelKey: 'contractForm.city', type: 'text', required: false, width: 'half' },
      { id: 'sys-address', labelKey: 'contractForm.address', type: 'text', required: false, width: 'full' },
    ],
  },
  {
    id: 'sys-dates',
    titleKey: 'contractForm.datesAndTimeline',
    subtitleKey: 'contractForm.datesAndTimelineSubtitle',
    color: '#10b981',
    fields: [
      { id: 'sys-startDate', labelKey: 'contractForm.startDate', type: 'date', required: false, width: 'half' },
      { id: 'sys-endDate', labelKey: 'contractForm.endDate', type: 'date', required: false, width: 'half' },
      { id: 'sys-signingDate', labelKey: 'contractForm.signingDate', type: 'date', required: false, width: 'half' },
      { id: 'sys-reminderDays', labelKey: 'contractForm.reminderDays', type: 'number', required: false, width: 'half' },
      { id: 'sys-autoRenewal', labelKey: 'contractForm.autoRenewal', type: 'checkbox', required: false, width: 'full' },
    ],
  },
  // NOTE: no "Financial" group here — financial fields are not fixed form
  // fields. They come from the Form Alanları Şablonu assigned to the contract
  // type (the standard financial template ships a Finansal Bilgiler section).
];

// Flat view, kept for anything that only cares about the field list.
export const SYSTEM_FIELDS = SYSTEM_SECTIONS.flatMap((s) => s.fields);
